// Components
import { Tooltip } from "@mantine/core";
import {
  IconCheck,
  IconFlag,
  IconPhoneOff,
  IconShield,
} from "@tabler/icons-react";
import { CardSelect } from "@benchmark/ui/components/atomic";
import { FormField } from "@benchmark/ui/components/atomic";

// Constants
import {
  FLAG_CODE_OPTIONS,
  FLAG_LOCAL_CODE_SYSTEM,
} from "@benchmark/core/constants";

// Types
import type { UseFormReturnType } from "@mantine/form";
import type { Flag } from "@medplum/fhirtypes";
import type { EnhancedSelectOption } from "@/types/ui/component-props";
import { useMemo, useCallback, type ReactNode } from "react";

/**
 * Props for FollowUpFlagsSection component.
 * @agentic connections
 */
type FollowUpFlagsSectionProps = {
  form: UseFormReturnType<{
    "followup-flags"?: string[];
    [key: string]: unknown;
  }>;
  patientFlags: Flag[];
};

/**
 * Enhanced patient flag options with icons for CardSelect. Excludes patient-paused and cocm-enrolled.
 * @agentic connections
 */
const LOCAL_FLAG_CARD_OPTIONS_WITH_ICONS: EnhancedSelectOption[] =
  FLAG_CODE_OPTIONS.filter(
    (option) =>
      option.system === FLAG_LOCAL_CODE_SYSTEM &&
      option.code !== "patient-paused" &&
      option.code !== "cocm-enrolled",
  ).map((option) => {
    let icon: ReactNode;
    switch (option.code) {
      case "clinician-contact-only":
        icon = <IconPhoneOff size={20} />;
        break;
      case "patient-under-18":
        icon = <IconShield size={20} />;
        break;
      default:
        icon = <IconFlag size={20} />;
    }

    return {
      value: option.code,
      label: option.display,
      description: option.description,
      icon,
    } as EnhancedSelectOption;
  });

/**
 * Form section for selecting follow-up flags (clinician-contact-only, patient-under-18, etc.).
 * @agentic connections
 */
export const FollowUpFlagsSection = ({
  form,
  patientFlags,
}: FollowUpFlagsSectionProps) => {
  // Create memoized flag options with disabled state for existing patient flags
  const flagOptionsWithDisabledState = useMemo(() => {
    // Get codes of existing active flags
    const existingFlagCodes = new Set(
      patientFlags
        .filter((flag) => flag.status === "active")
        .flatMap((flag) =>
          (flag.code?.coding || []).map((c) => c.code).filter(Boolean),
        ),
    );

    return LOCAL_FLAG_CARD_OPTIONS_WITH_ICONS.map((option) => ({
      ...option,
      disabled: existingFlagCodes.has(option.value),
    }));
  }, [patientFlags]);

  // Handler for flag selection that matches CardSelect's onChange signature
  const handleFlagChange = useCallback(
    (value: string | string[] | null) => {
      form.setFieldValue(
        "followup-flags",
        Array.isArray(value) ? (value as string[]) : value ? [value] : [],
      );
    },
    [form],
  );

  // Custom renderCard function that wraps disabled cards with Tooltip
  const renderFlagCard = useCallback(
    (option: EnhancedSelectOption, selected: boolean) => {
      const handleCardClick = () => {
        if (option.disabled) return;
        const currentFlags =
          (form.values["followup-flags"] as string[] | undefined) || [];
        if (currentFlags.includes(option.value)) {
          handleFlagChange(currentFlags.filter((v) => v !== option.value));
        } else {
          handleFlagChange([...currentFlags, option.value]);
        }
      };

      // Create a card element matching CardSelect's structure
      const cardElement = (
        <div
          key={option.value}
          onClick={handleCardClick}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !option.disabled) {
              e.preventDefault();
              handleCardClick();
            }
          }}
          style={{
            display: "flex",
            alignItems: "center",
            padding: "16px",
            border: selected
              ? "1px solid var(--mantine-color-purple-6)"
              : "1px solid var(--mantine-color-gray-3)",
            borderRadius: "8px",
            cursor: option.disabled ? "not-allowed" : "pointer",
            transition: "all 0.2s ease",
            backgroundColor: selected
              ? "var(--mantine-color-purple-1)"
              : "white",
            opacity: option.disabled ? 0.6 : 1,
            pointerEvents: option.disabled ? "none" : "auto",
            boxShadow: selected
              ? "0 0 0 1px var(--mantine-color-purple-6)"
              : "none",
          }}
          tabIndex={option.disabled ? -1 : 0}
          role="checkbox"
          aria-checked={selected}
          aria-disabled={option.disabled}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
            }}
          >
            {/* Selection Indicator */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "20px",
                height: "20px",
                border: selected
                  ? "2px solid var(--mantine-color-purple-6)"
                  : "2px solid var(--mantine-color-gray-4)",
                borderRadius: "4px",
                backgroundColor: selected
                  ? "var(--mantine-color-purple-6)"
                  : "white",
                flexShrink: 0,
              }}
            >
              {selected && <IconCheck size={16} color="white" />}
            </div>

            {/* Option Icon */}
            {option.icon && <div>{option.icon}</div>}

            {/* Text Content */}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500 }}>{option.label}</div>
              {option.description && (
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: "var(--mantine-color-gray-6)",
                  }}
                >
                  {option.description}
                </div>
              )}
            </div>
          </div>
        </div>
      );

      // Wrap disabled cards with Tooltip
      if (option.disabled) {
        return (
          <Tooltip
            key={option.value}
            label="This flag is already active on the patient"
            withArrow
          >
            <div>{cardElement}</div>
          </Tooltip>
        );
      }

      return cardElement;
    },
    [form, handleFlagChange],
  );

  return (
    <FormField
      label="Follow-Up Flags"
      variant="section"
      showDivider={false}
      description="Add relevant member flags (optional)"
    >
      <CardSelect
        multiple
        columns={2}
        value={(form.values["followup-flags"] as string[] | undefined) || []}
        onChange={(value) =>
          form.setFieldValue(
            "followup-flags",
            Array.isArray(value) ? (value as string[]) : value ? [value] : [],
          )
        }
        data={flagOptionsWithDisabledState}
        renderCard={renderFlagCard}
      />
    </FormField>
  );
};
